// Shared low-poly wildland parts (coast, desert, mountain & snow creatures) —
// `tube` lays a low-poly taper between two arbitrary points (the workhorse for
// bent bug legs, horns, antlers, necks and tails — no hand-derived Euler angles
// at the call site); `wingPair` builds a mirrored pair of layered soaring wings
// (inner panel, lifted swept outer panel, separated finger feathers) shared by
// the gull, eagle, vulture and snowy owl; `eyes` is the mirrored eye-dot pair
// nearly every head wants. Ported from age-of-mcp's v2/wildland-parts.ts: the
// pure geometry is kept verbatim, with the Part type taken from the library.

import * as THREE from "three";
import type { Part } from "../types";

export type Vec3 = readonly [number, number, number];

/** A mirrored pair of round eye dots at z = ±z. */
export function eyes(
  x: number, y: number, z: number,
  r = 0.0035, color: THREE.ColorRepresentation = "#16130e",
): Part[] {
  return [z, -z].map((zz): Part => ({
    geometry: new THREE.IcosahedronGeometry(r, 0), color, position: [x, y, zz],
  }));
}

/**
 * A low-poly taper laid from `from` to `to` (radius `rFrom` at the start,
 * `rTo` at the end). Orientation solved with the XYZ-euler identity
 * R(rx, 0, rz) · ŷ = (−sin rz, cos rz·cos rx, cos rz·sin rx), so any
 * direction is reachable with two angles.
 */
export function tube(
  from: Vec3, to: Vec3, rFrom: number, rTo: number,
  color: THREE.ColorRepresentation, radial = 4,
): Part {
  const dx = to[0] - from[0], dy = to[1] - from[1], dz = to[2] - from[2];
  const len = Math.hypot(dx, dy, dz) || 1e-5;
  const rz = Math.asin(Math.max(-1, Math.min(1, -dx / len)));
  const rx = Math.atan2(dz / len, dy / len);
  return {
    geometry: new THREE.CylinderGeometry(rTo, rFrom, len, radial),
    color,
    position: [from[0] + dx / 2, from[1] + dy / 2, from[2] + dz / 2],
    rotation: [rx, 0, rz],
  };
}

export interface WingOpts {
  /** Wing root: x, y and the z magnitude where each wing leaves the body. */
  root: Vec3;
  /** Root→tip reach of the panels along ±z (fingers extend a bit past it). */
  span: number;
  /** Inner-panel depth along x (the outer panel narrows from it). */
  chord: number;
  /** How far the tip lifts above the root (soaring dihedral). */
  rise: number;
  /** How far the outer panel sweeps back toward −x. */
  sweep: number;
  /** Panel thickness. */
  thick: number;
  inner: THREE.ColorRepresentation;
  outer: THREE.ColorRepresentation;
  /** Finger-feather colour (defaults to `outer`). */
  tip?: THREE.ColorRepresentation;
  /** Separated wingtip feathers per wing (0 = rounded, finger-less wing). */
  fingers?: number;
}

/** A mirrored pair of layered soaring wings: inner panel, lifted swept outer
 *  panel and optional separated finger feathers fanning back at the tip. */
export function wingPair(o: WingOpts): Part[] {
  const parts: Part[] = [];
  const inLen = o.span * 0.46;
  const outLen = o.span * 0.54;
  const tipColor = o.tip ?? o.outer;
  for (const s of [1, -1] as const) {
    const tiltIn = Math.atan2(o.rise * 0.3, inLen);
    parts.push({
      geometry: new THREE.BoxGeometry(o.chord, o.thick, inLen),
      color: o.inner,
      position: [o.root[0], o.root[1] + o.rise * 0.15, s * (o.root[2] + inLen * 0.5)],
      rotation: [-s * tiltIn, 0, 0],
    });
    const tiltOut = Math.atan2(o.rise * 0.7, outLen);
    const sweepAng = Math.atan2(o.sweep, outLen);
    parts.push({
      geometry: new THREE.BoxGeometry(o.chord * 0.74, o.thick * 0.85, outLen),
      color: o.outer,
      position: [o.root[0] - o.sweep * 0.5, o.root[1] + o.rise * 0.6,
        s * (o.root[2] + inLen + outLen * 0.46)],
      rotation: [-s * tiltOut, -s * sweepAng, 0],
    });
    const nf = o.fingers ?? 0;
    for (let i = 0; i < nf; i++) {
      const back = 0.2 + i * 0.32;             // each finger fans further back
      const fl = o.span * (0.3 - i * 0.05);
      parts.push({
        geometry: new THREE.ConeGeometry(o.thick * 0.85, fl, 3),
        color: tipColor,
        position: [
          o.root[0] - o.sweep - Math.sin(back) * fl * 0.45,
          o.root[1] + o.rise * 0.97,
          s * (o.root[2] + o.span * 0.93 + Math.cos(back) * fl * 0.45),
        ],
        rotation: [s * Math.PI / 2, 0, back],
      });
    }
  }
  return parts;
}
